'use client';

import { useApolloClient } from '@apollo/client';
import React, { createContext, useContext, useMemo, useState } from 'react';

import { useChewyApiContext } from '../Apollo/ApolloChewyApiProvider';

import { RatingFilter, ReviewSort } from '@/apollo/__generated__/global-types';
import {
  FreshProductFragment,
  FreshProductReviewFragment,
  FreshProductsDocument,
  FreshProductsQuery,
} from '@/integrations/chewy-api/__generated__/chewy-api.fresh-products';

export interface ReviewsContextValues {
  averageOverallRating: number | undefined;
  fetchReviews: (sort?: ReviewSort, filter?: RatingFilter, partNumbers?: string[]) => Promise<void>;
  getMoreReviews: () => Promise<void>;
  hasMoreReviews: boolean;
  reviews: FreshProductReviewFragment[];
  totalReviewCount: number | undefined;
  ratingFilter: string | undefined;
  sortOption: string | undefined;
  setRatingFilter: (val: RatingFilter | undefined) => void;
  setSortOption: (val: ReviewSort | undefined) => void;
  selectedPartNumbers: string[];
  setSelectedPartNumbers: (val: string[]) => void;
}

export const defaultReviewsContext: ReviewsContextValues = {
  averageOverallRating: 0,
  fetchReviews: async () => {},
  getMoreReviews: async () => {},
  hasMoreReviews: true,
  reviews: [],
  totalReviewCount: 0,
  ratingFilter: undefined,
  sortOption: undefined,
  setRatingFilter: () => {},
  setSortOption: () => {},
  selectedPartNumbers: [],
  setSelectedPartNumbers: () => {},
};

export const ReviewsContext = createContext<ReviewsContextValues>(defaultReviewsContext);

const PAGE_SIZE = 10;

// used to re-sort reviews after fetching because we're combining reviews from multiple products
const sortReviews = (
  reviews: FreshProductReviewFragment[],
  sortOption?: ReviewSort,
): FreshProductReviewFragment[] => {
  if (!sortOption) return reviews;
  const sorted = [...reviews];
  switch (sortOption) {
    case 'NEWEST': {
      sorted.sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
      break;
    }
    case 'OLDEST': {
      sorted.sort((a, b) => new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime());
      break;
    }
    case 'HIGHEST_RATING': {
      sorted.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
      break;
    }
    case 'LOWEST_RATING': {
      sorted.sort((a, b) => (a.rating ?? 0) - (b.rating ?? 0));
      break;
    }
    default: {
      break;
    }
  }
  return sorted;
};

export const ReviewsProvider = ({
  averageOverallRating,
  totalReviewCount,
  children,
  initialReviews,
}: {
  averageOverallRating?: number;
  totalReviewCount?: number;
  children: React.ReactNode;
  initialReviews?: FreshProductReviewFragment[];
}) => {
  const client = useApolloClient();
  const { products } = useChewyApiContext();

  const [ratingFilter, setRatingFilter] = useState<RatingFilter | undefined>();
  const [sortOption, setSortOption] = useState<ReviewSort | undefined>(ReviewSort.Newest);
  const [selectedPartNumbers, setSelectedPartNumbers] = useState<string[]>(
    products?.map((product) => product.partNumber) ?? [],
  );
  const [page, setPage] = useState(0);
  const [reviews, setReviews] = useState<FreshProductReviewFragment[]>(initialReviews ?? []);
  const [lastFetchedProducts, setLastFetchedProducts] = useState<
    FreshProductFragment[] | null | undefined
  >(products);

  const fetchReviews = async (
    sort?: ReviewSort,
    filter?: RatingFilter,
    partNumbers?: string[],
    pageToUse = 0,
  ) => {
    setSortOption(sort);
    setRatingFilter(filter);
    setSelectedPartNumbers(partNumbers ?? products?.map((p) => p.partNumber) ?? []);
    setPage(pageToUse);

    const { data } = await client.query<FreshProductsQuery>({
      query: FreshProductsDocument,
      variables: {
        pageRequestInput: { page: pageToUse, size: PAGE_SIZE },
        sort,
        filter: {
          ratingFilter: filter,
        },
      },
      fetchPolicy: 'network-only',
    });

    // post fetch filtering by part numbers
    // api does not support filtering by part numbers directly
    const filteredProducts =
      data?.freshProducts?.filter((product) =>
        partNumbers && partNumbers.length < (products?.length ?? 0)
          ? partNumbers.includes(product.partNumber)
          : true,
      ) ?? [];

    setLastFetchedProducts(filteredProducts);

    // remove null reviews
    const allReviews = filteredProducts.flatMap(
      (product) =>
        product.reviewsPage?.results?.filter(
          (r) => r.reviewText !== null && r.reviewText !== undefined,
        ) ?? [],
    );

    // re-sort combined reviews
    setReviews(sortReviews(allReviews, sort));
  };

  const getMoreReviews = async () => {
    const nextPage = page + 1;
    setPage(nextPage);

    const { data } = await client.query<FreshProductsQuery>({
      query: FreshProductsDocument,
      variables: {
        pageRequestInput: { page: nextPage, size: PAGE_SIZE },
        sort: sortOption,
        filter: {
          ratingFilter,
        },
      },
      fetchPolicy: 'network-only',
    });

    const filteredProducts =
      data?.freshProducts?.filter((product) =>
        selectedPartNumbers && selectedPartNumbers.length < (products?.length ?? 0)
          ? selectedPartNumbers.includes(product.partNumber)
          : true,
      ) ?? [];

    setLastFetchedProducts(filteredProducts);

    const moreReviews = filteredProducts.flatMap(
      (product) =>
        product.reviewsPage?.results
          ?.filter((r) => r.reviewText !== null && r.reviewText !== undefined)
          .map((r) => ({ ...r, partNumber: product.partNumber })) ?? [],
    );
    setReviews((prev) => sortReviews([...prev, ...moreReviews], sortOption));
  };

  // if any product in the last fetched set has more reviews, we have more to load
  const hasMoreReviews = useMemo(
    () => lastFetchedProducts?.some((product) => product.reviewsPage?.hasNextPage) ?? false,
    [lastFetchedProducts],
  );

  return (
    <ReviewsContext.Provider
      value={{
        averageOverallRating,
        fetchReviews,
        getMoreReviews,
        hasMoreReviews,
        reviews,
        totalReviewCount,
        ratingFilter,
        sortOption,
        setRatingFilter,
        setSortOption,
        selectedPartNumbers,
        setSelectedPartNumbers,
      }}
    >
      {children}
    </ReviewsContext.Provider>
  );
};

export const useReviewsContext = () => useContext(ReviewsContext);
